import axios from "axios";
import { getAccessToken } from "./authSession";
import { API_BASE_URL } from "../config";

const getAuthHeaders = () => {
    const token = getAccessToken();
    if (!token) {
        window.location.href = "/";
    }
    return {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
    };
};

const getMultipartAuthHeaders = () => {
    const token = getAccessToken();
    if (!token) {
        window.location.href = "/";
    }
    return {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };
};

const periodParams = ({ periodFrom, periodTo, businessEntityId, tab }) => ({
    period_from: periodFrom,
    period_to: periodTo,
    ...(businessEntityId ? { business_entity_id: businessEntityId } : {}),
    ...(tab ? { tab } : {}),
});

export const getFinanceOverview = async ({ periodFrom, periodTo, businessEntityId }) => {
    const response = await axios.get(`${API_BASE_URL}/finance-reports/overview`, {
        ...getAuthHeaders(),
        params: periodParams({ periodFrom, periodTo, businessEntityId }),
    });
    return response.data;
};

export const getFinanceTabReport = async ({ tab, periodFrom, periodTo, businessEntityId }) => {
    const pathByTab = {
        pnl: "pnl",
        cash_flow: "cash-flow",
        deposits: "deposits",
        supplier_payments: "supplier-payments",
        capital: "capital",
    };
    const response = await axios.get(`${API_BASE_URL}/finance-reports/${pathByTab[tab] || "pnl"}`, {
        ...getAuthHeaders(),
        params: periodParams({ periodFrom, periodTo, businessEntityId }),
    });
    return response.data;
};

export const recalculateFinanceReport = async ({ periodFrom, periodTo, businessEntityId }) => {
    const response = await axios.post(
        `${API_BASE_URL}/finance-reports/recalculate`,
        { period_from: periodFrom, period_to: periodTo },
        {
            ...getAuthHeaders(),
            params: businessEntityId ? { business_entity_id: businessEntityId } : {},
        },
    );
    return response.data;
};

export const upsertFinanceReportValue = async (payload) => {
    const response = await axios.put(`${API_BASE_URL}/finance-reports/values`, payload, getAuthHeaders());
    return response.data;
};

export const upsertFinanceReportNote = async (payload) => {
    const response = await axios.put(`${API_BASE_URL}/finance-reports/notes`, payload, getAuthHeaders());
    return response.data;
};

export const upsertFinanceCapitalMovement = async (payload) => {
    const response = await axios.put(`${API_BASE_URL}/finance-reports/capital-movements`, payload, getAuthHeaders());
    return response.data;
};

export const deleteFinanceCapitalMovement = async (movementId) => {
    const response = await axios.delete(`${API_BASE_URL}/finance-reports/capital-movements/${movementId}`, getAuthHeaders());
    return response.data;
};

export const getFinanceLegalEntitySettings = async () => {
    const response = await axios.get(`${API_BASE_URL}/finance-reports/legal-entity-settings`, getAuthHeaders());
    return response.data || [];
};

export const upsertFinanceLegalEntitySetting = async (payload) => {
    const response = await axios.put(`${API_BASE_URL}/finance-reports/legal-entity-settings`, payload, getAuthHeaders());
    return response.data;
};

export const getFinanceFixedCharges = async () => {
    const response = await axios.get(`${API_BASE_URL}/finance-reports/fixed-charges`, getAuthHeaders());
    return response.data || [];
};

export const upsertFinanceFixedCharge = async (payload) => {
    const response = await axios.put(`${API_BASE_URL}/finance-reports/fixed-charges`, payload, getAuthHeaders());
    return response.data;
};

export const getFinanceFulfillmentSuppliers = async () => {
    const response = await axios.get(`${API_BASE_URL}/finance-reports/fulfillment-suppliers`, getAuthHeaders());
    return response.data || [];
};

export const upsertFinanceFulfillmentSupplier = async (payload) => {
    const response = await axios.put(`${API_BASE_URL}/finance-reports/fulfillment-suppliers`, payload, getAuthHeaders());
    return response.data;
};

export const importFinanceExcel = async (payload) => {
    const response = await axios.post(`${API_BASE_URL}/finance-reports/import/excel`, payload, getAuthHeaders());
    return response.data;
};

export const uploadFinanceExcel = async ({ file, businessEntityId }) => {
    const formData = new FormData();
    formData.append("file", file);
    if (businessEntityId) {
        formData.append("business_entity_id", businessEntityId);
    }
    const response = await axios.post(
        `${API_BASE_URL}/finance-reports/import/excel-upload`,
        formData,
        getMultipartAuthHeaders(),
    );
    return response.data;
};

export const downloadFinanceExport = async ({ periodFrom, periodTo, businessEntityId, tab }) => {
    const response = await axios.get(`${API_BASE_URL}/finance-reports/export.xlsx`, {
        ...getAuthHeaders(),
        params: periodParams({ periodFrom, periodTo, businessEntityId, tab }),
        responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "finance_report.xlsx");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};
